import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import apiClient from "utils/apiClient";
import { getAuthToken } from "utils/authToken";
import { ENDPOINTS } from "utils/endpoints";

import { RED, RED_LIGHT, BORDER, MUTED } from "assets/theme/brandColors";
import { buildAvoidOptions, suggestAvoidOptions } from "utils/avoidOptions";
import DietSelector from "components/DietSelector";
import { DIET_NONE, excludedGroupsFor, useDietPreference } from "utils/dietPreference";
const TOTAL = 5;

const MOTIVATIONS = [
  "Save money",
  "Save time",
  "Eat healthy",
  "Make cooking feel calm",
  "Actually learn to cook",
  "Waste less food",
  "Get smarter about my kitchen",
];

const Btn = ({ children, onClick, variant = "primary", sx = {} }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      py: "13px",
      px: variant === "back" ? "18px" : undefined,
      flex: variant === "back" ? "0 0 auto" : 1,
      bgcolor: variant === "back" ? "#f5f5f5" : RED,
      color: variant === "back" ? "#666" : "#fff",
      border: "none",
      borderRadius: "10px",
      fontSize: 14,
      fontWeight: variant === "back" ? 600 : 700,
      cursor: "pointer",
      fontFamily: "Roboto, sans-serif",
      transition: "background 0.15s",
      "&:hover": { bgcolor: variant === "back" ? "#ebebeb" : "#7d1520" },
      ...sx,
    }}
  >
    {children}
  </Box>
);

Btn.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  variant: PropTypes.string,
  sx: PropTypes.object,
};

function Dots({ current }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", gap: "7px", mb: 4 }}>
      {Array.from({ length: TOTAL }, (_, i) => (
        <Box
          key={i}
          sx={{
            height: "7px",
            width: i + 1 === current ? "20px" : "7px",
            borderRadius: i + 1 === current ? "4px" : "50%",
            bgcolor: i + 1 === current ? RED : i + 1 < current ? "#ccc" : "#ddd",
            transition: "all 0.2s",
          }}
        />
      ))}
    </Box>
  );
}
Dots.propTypes = { current: PropTypes.number.isRequired };

const card = {
  bgcolor: "#fff",
  borderRadius: "18px",
  border: "1.5px solid",
  borderColor: BORDER,
  p: "36px 32px",
};

function Welcome({ onNext }) {
  return (
    <Box sx={{ maxWidth: 480, width: "100%" }}>
      <Box sx={{ ...card, textAlign: "center" }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            bgcolor: RED,
            borderRadius: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 28,
            mx: "auto",
            mb: 2.5,
          }}
        >
          🍳
        </Box>
        <Box
          sx={{
            fontSize: 24,
            fontWeight: 800,
            color: "#222",
            mb: 1.25,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Welcome to Estia
        </Box>
        <Box
          sx={{
            fontSize: 14,
            color: "#666",
            lineHeight: 1.65,
            mb: 3.5,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          A smarter way to cook. Build weekly schedules around your life, follow along step by step,
          and actually get better in the kitchen.
        </Box>
        <Btn onClick={onNext} sx={{ width: "100%" }}>
          Get started
        </Btn>
      </Box>
    </Box>
  );
}
Welcome.propTypes = { onNext: PropTypes.func.isRequired };

const CIRCLE_STEPS = [
  { icon: "📅", num: "1", label: "Build a schedule", left: "230px", top: "80px" },
  { icon: "🛒", num: "2", label: "Shop once", left: "380px", top: "230px" },
  { icon: "🍳", num: "3", label: "Cook & understand", left: "230px", top: "380px" },
  { icon: "⚡", num: "4", label: "Cook by instinct", left: "80px", top: "230px" },
];

const ARROWS = [
  { left: "336px", top: "124px", rotate: "45deg" },
  { left: "336px", top: "336px", rotate: "135deg" },
  { left: "124px", top: "336px", rotate: "225deg" },
  { left: "124px", top: "124px", rotate: "315deg" },
];

function HowItWorks({ onNext, onBack }) {
  return (
    <Box sx={{ maxWidth: 620, width: "100%" }}>
      <Box sx={{ ...card, p: "32px 28px" }}>
        <Box
          sx={{
            fontSize: 18,
            fontWeight: 800,
            color: "#222",
            mb: 0.75,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Here&apos;s how Estia works
        </Box>
        <Box
          sx={{
            fontSize: 13,
            color: "#888",
            lineHeight: 1.55,
            mb: 3,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          You don&apos;t just follow recipes — you start understanding them. Over time you&apos;ll
          know how to adapt and cook with whatever you have.
        </Box>

        {/* Circle diagram */}
        <Box sx={{ width: "100%", overflow: "hidden", mb: { xs: 0, sm: 3 } }}>
          <Box
            sx={{
              position: "relative",
              width: "460px",
              height: "460px",
              left: "50%",
              transform: { xs: "translateX(-50%) scale(0.79)", sm: "translateX(-50%)" },
              transformOrigin: "center center",
            }}
          >
            <svg style={{ position: "absolute", top: 0, left: 0 }} width="460" height="460">
              <circle
                cx="230"
                cy="230"
                r="150"
                fill="none"
                stroke="#e89aa4"
                strokeWidth="3"
                strokeDasharray="7 4"
              />
            </svg>

            {CIRCLE_STEPS.map((s) => (
              <Box
                key={s.num}
                sx={{
                  position: "absolute",
                  left: s.left,
                  top: s.top,
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                  width: "96px",
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    bgcolor: RED_LIGHT,
                    borderRadius: "14px",
                    fontSize: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: "5px",
                  }}
                >
                  {s.icon}
                </Box>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    bgcolor: "#333",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 900,
                    lineHeight: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: "3px",
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  {s.num}
                </Box>
                <Box
                  sx={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: "#222",
                    lineHeight: 1.3,
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  {s.label}
                </Box>
              </Box>
            ))}

            {ARROWS.map((a, i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  left: a.left,
                  top: a.top,
                  transform: `translate(-50%, -50%) rotate(${a.rotate})`,
                  color: "#e07080",
                  fontSize: "16px",
                  lineHeight: 1,
                }}
              >
                ▶
              </Box>
            ))}

            <Box
              sx={{
                position: "absolute",
                left: "230px",
                top: "230px",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                width: "136px",
                bgcolor: RED_LIGHT,
                borderRadius: "50%",
                border: "2px solid",
                borderColor: "#f5c0c5",
                aspectRatio: "1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: RED,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  lineHeight: 1.5,
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                Understand food, not just recipes
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Btn variant="back" onClick={onBack}>
            Back
          </Btn>
          <Btn onClick={onNext}>Sounds good →</Btn>
        </Box>
      </Box>
    </Box>
  );
}
HowItWorks.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

function Motivations({ selected, onToggle, onNext, onBack }) {
  return (
    <Box sx={{ maxWidth: 480, width: "100%" }}>
      <Box sx={card}>
        <Box
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.2px",
            color: RED,
            mb: 1,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Quick question
        </Box>
        <Box
          sx={{
            fontSize: 20,
            fontWeight: 800,
            color: "#222",
            mb: 2.25,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          I want to…
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "9px", mb: 3.5 }}>
          {MOTIVATIONS.map((m) => {
            const active = selected.includes(m);
            return (
              <Box
                key={m}
                component="button"
                onClick={() => onToggle(m)}
                sx={{
                  padding: "9px 16px",
                  borderRadius: "30px",
                  border: "1.5px solid",
                  borderColor: active ? RED : "#e0e0e0",
                  bgcolor: active ? RED_LIGHT : "#fff",
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? RED : "#444",
                  cursor: "pointer",
                  fontFamily: "Roboto, sans-serif",
                  transition: "all 0.15s",
                  "&:hover": { borderColor: RED, color: RED },
                }}
              >
                {m}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Btn variant="back" onClick={onBack}>
            Back
          </Btn>
          <Btn onClick={onNext}>Continue</Btn>
        </Box>
      </Box>
    </Box>
  );
}
Motivations.propTypes = {
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

// Diet first, then the avoid list. The order is the point: this screen used to
// ask only "what do you avoid?", so a vegetarian had to think of every protein
// herself and name them one at a time. She answers once at the top now, and the
// list below is for what a diet has no opinion about — nuts, mushrooms, tomato.
// The family chips underneath drop whatever the diet already covers, so the two
// controls never appear to disagree about who is in charge of meat.
function Allergies({
  diet,
  dietOptions,
  onDietChange,
  selected,
  onToggle,
  onSearch,
  suggestions,
  onNext,
  onBack,
}) {
  return (
    <Box sx={{ maxWidth: 480, width: "100%" }}>
      <Box sx={card}>
        <Box
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.2px",
            color: RED,
            mb: 1,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Almost done
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: 20,
            fontWeight: 800,
            color: "#222",
            mb: 1.75,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          How do you eat?
          <Tooltip
            title="We'll only ever build schedules from food you actually eat. You can change this any time in your profile."
            enterTouchDelay={0}
            leaveTouchDelay={6000}
          >
            <Icon sx={{ fontSize: 16, color: "#999", cursor: "help" }} aria-label="Why we ask this">
              help_outline
            </Icon>
          </Tooltip>
        </Box>

        <Box sx={{ mb: 3 }}>
          <DietSelector value={diet} options={dietOptions} onChange={onDietChange} />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: 15,
            fontWeight: 700,
            color: "#222",
            mb: 1.5,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Anything you avoid?
          <Tooltip
            title="Allergies, dislikes, whatever. We'll keep them out of your schedules — always double-check, recipes are AI-generated."
            enterTouchDelay={0}
            leaveTouchDelay={6000}
          >
            <Icon sx={{ fontSize: 16, color: "#999", cursor: "help" }} aria-label="Why we ask this">
              help_outline
            </Icon>
          </Tooltip>
        </Box>

        {selected.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: 1.5 }}>
            {selected.map((item) => (
              <Box
                key={item.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  pl: "12px",
                  pr: "8px",
                  py: "5px",
                  bgcolor: RED_LIGHT,
                  borderRadius: "20px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: RED,
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                {item.name}
                <Box
                  component="button"
                  onClick={() => onToggle(item)}
                  sx={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: RED,
                    fontSize: 13,
                    lineHeight: 1,
                    p: 0,
                  }}
                >
                  ✕
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Box
          component="input"
          placeholder="Search foods to avoid — try “fish” or “mushroom”…"
          onChange={(e) => onSearch(e.target.value)}
          sx={{
            width: "100%",
            p: "11px 14px",
            border: "1.5px solid",
            borderColor: BORDER,
            borderRadius: "9px",
            fontSize: 13,
            fontFamily: "Roboto, sans-serif",
            color: "#333",
            outline: "none",
            mb: suggestions.length ? 1 : 2.5,
            "&:focus": { borderColor: RED },
          }}
        />

        {suggestions.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: 2.5 }}>
            {suggestions.map((item) => (
              <Box
                key={item.key}
                component="button"
                onClick={() => onToggle(item)}
                sx={{
                  padding: "7px 14px",
                  borderRadius: "30px",
                  border: "1.5px solid #e0e0e0",
                  bgcolor: "#fff",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#444",
                  cursor: "pointer",
                  fontFamily: "Roboto, sans-serif",
                  "&:hover": { borderColor: RED, color: RED },
                }}
              >
                {item.kind === "group" ? `${item.name} (all ${item.memberCount})` : item.name}
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Btn variant="back" onClick={onBack}>
            Back
          </Btn>
          <Btn onClick={onNext}>Continue</Btn>
        </Box>
        <Box
          component="button"
          onClick={onNext}
          sx={{
            display: "block",
            width: "100%",
            mt: 1.5,
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 12,
            color: "#bbb",
            fontFamily: "Roboto, sans-serif",
            textDecoration: "underline",
            "&:hover": { color: MUTED },
          }}
        >
          Skip for now
        </Box>
      </Box>
    </Box>
  );
}
Allergies.propTypes = {
  diet: PropTypes.string.isRequired,
  dietOptions: PropTypes.array.isRequired,
  onDietChange: PropTypes.func.isRequired,
  selected: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  suggestions: PropTypes.array.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

function Done({ onFinish }) {
  return (
    <Box sx={{ maxWidth: 480, width: "100%" }}>
      <Box sx={{ ...card, textAlign: "center" }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            bgcolor: RED_LIGHT,
            borderRadius: "16px",
            fontSize: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2.5,
          }}
        >
          ✅
        </Box>
        <Box
          sx={{
            fontSize: 22,
            fontWeight: 800,
            color: "#222",
            mb: 1,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          You&apos;re all set
        </Box>
        <Box
          sx={{
            fontSize: 14,
            color: "#666",
            lineHeight: 1.65,
            mb: 3.5,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Time to build your first schedule. It takes about 2 minutes.
        </Box>
        <Btn onClick={onFinish} sx={{ width: "100%" }}>
          Build my first schedule →
        </Btn>
      </Box>
    </Box>
  );
}
Done.propTypes = { onFinish: PropTypes.func.isRequired };

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [motivations, setMotivations] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [query, setQuery] = useState("");
  // Read for the vocabulary; the answer is held locally and posted with the
  // rest of onboarding rather than PATCHed as the user taps. /submit writes it
  // before it seeds goals, which is what stops a vegetarian's first goal being
  // "Chicken Whisperer" — a separate PATCH would race that ordering.
  const { options: dietOptions } = useDietPreference();
  const [diet, setDiet] = useState(DIET_NONE);
  // Whether the server has answered "have you been through this?" yet. Nothing
  // renders until it has, so a returning user never sees step 1 flash past.
  const [checkedStatus, setCheckedStatus] = useState(false);

  // Whether onboarding is done is the server's answer, not the browser's.
  //
  // This used to read a localStorage flag ("onboarding_done") written on the
  // last step. It was global to the browser and keyed to nobody: delete the
  // account, sign up again with the same address, and the new user — a real
  // new row, with no diet, no avoidances and goals about to be seeded blind —
  // was bounced straight to /dashboard by a flag their predecessor set. Three
  // testers concluded onboarding simply never ran.
  //
  // GET /api/onboarding/status answers it from that user's own rows, which is
  // the same source sign-in already routes on, so the two cannot disagree. It
  // also survives what the flag could not: a second browser, a cleared cache,
  // a tab closed halfway through.
  //
  // On failure show the form rather than skip it. /submit is idempotent
  // ("already_done"), so a returning user re-answering costs them a minute;
  // skipping a real new user costs them their preferences with nothing said.
  useEffect(() => {
    if (!getAuthToken()) {
      navigate("/authentication/sign-in");
      return undefined;
    }
    let cancelled = false;
    apiClient
      .get(ENDPOINTS.onboarding.status)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.completed === true) navigate("/dashboard", { replace: true });
        else setCheckedStatus(true);
      })
      .catch(() => {
        if (!cancelled) setCheckedStatus(true);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    apiClient
      .get(ENDPOINTS.pantry.ingredientNameList)
      .then((r) => setIngredients(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    apiClient
      .get(ENDPOINTS.pantry.ingredientGroups)
      .then((r) => setGroups(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  // Same list and same matching rules as the Profile picker — see
  // utils/avoidOptions.js. Two screens that must offer the same thing.
  const avoidOptions = useMemo(
    () => buildAvoidOptions(groups, ingredients, excludedGroupsFor(diet, dietOptions)),
    [groups, ingredients, diet, dietOptions]
  );

  const toggleMotivation = (m) =>
    setMotivations((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  const toggleBlacklist = (item) =>
    setBlacklist((prev) =>
      prev.find((x) => x.key === item.key)
        ? prev.filter((x) => x.key !== item.key)
        : [...prev, item]
    );

  // With no query, offer the families. buildAvoidOptions puts them first and
  // there are far fewer of them than ingredients, so this is the group list —
  // which is the whole answer to "I don't eat meat" in three taps instead of
  // fifteen. Typing then searches everything, families included.
  const suggestions = query.trim()
    ? suggestAvoidOptions(avoidOptions, blacklist, query, 8)
    : suggestAvoidOptions(avoidOptions, blacklist, "", 8).filter((o) => o.kind === "group");

  const handleSubmit = async () => {
    try {
      await apiClient.post(ENDPOINTS.onboarding.submit, {
        blacklisted_ingredients: blacklist.filter((i) => i.kind !== "group").map((i) => i.id),
        // Groups go over as slugs. Sending the members instead would freeze
        // today's catalog into the answer, and a fish added next month would
        // slip past an exclusion the user set on day one.
        blacklisted_groups: blacklist.filter((i) => i.kind === "group").map((i) => i.slug),
        pantry_items: [],
        motivations,
        // Written before /submit seeds the first goal in each track, which is
        // the ordering that keeps a vegetarian off the ingredient track's
        // chicken goal.
        diet,
      });
    } catch {
      // non-blocking
    }
    setStep(5);
  };

  // Held until the status check lands. Showing step 1 and then redirecting is
  // how a returning user watches a form they already filled in flash past.
  if (!checkedStatus) return null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f7f4f0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Dots current={step} />

      {step === 1 && <Welcome onNext={() => setStep(2)} />}
      {step === 2 && <HowItWorks onNext={() => setStep(3)} onBack={() => setStep(1)} />}
      {step === 3 && (
        <Motivations
          selected={motivations}
          onToggle={toggleMotivation}
          onNext={() => setStep(4)}
          onBack={() => setStep(2)}
        />
      )}
      {step === 4 && (
        <Allergies
          diet={diet}
          dietOptions={dietOptions}
          onDietChange={(next) => {
            setDiet(next);
            // Drop family picks the new diet already covers. Leaving them would
            // show chips for exclusions the user can no longer find in the
            // picker, which reads as the two controls disagreeing.
            const covered = new Set(excludedGroupsFor(next, dietOptions));
            setBlacklist((prev) =>
              prev.filter((i) => !(i.kind === "group" && covered.has(i.slug)))
            );
          }}
          selected={blacklist}
          onToggle={(item) => {
            toggleBlacklist(item);
            setQuery("");
          }}
          onSearch={setQuery}
          suggestions={suggestions}
          onNext={handleSubmit}
          onBack={() => setStep(3)}
        />
      )}
      {/* Nothing is written on finish. Completion is a fact about the user's
          rows, recorded by /submit above and read back from the server — not a
          mark left in this browser. */}
      {step === 5 && <Done onFinish={() => navigate("/schedule-builder")} />}
    </Box>
  );
}
